import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/content-page";
import { CaseStudiesSection } from "@/components/case-studies-section";
import { getAllCaseStudies } from "@/lib/case-studies.functions";
import type { CaseStudy } from "@/lib/sanity";
import { seo } from "@/lib/seo";
import { collectionPage, breadcrumb } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";

type LoaderData = { caseStudies: CaseStudy[] };

export const Route = createFileRoute("/case-studies/")({
  loader: async (): Promise<LoaderData> => {
    const caseStudies = await getAllCaseStudies();
    return { caseStudies };
  },
  head: ({ loaderData }) => {
    const studies = (loaderData as LoaderData | undefined)?.caseStudies ?? [];
    return seo({
      title: "AI Marketing Case Studies & Customer Review",
      description:
        "How venture-backed startups and agencies use Axy.digital to scale GEO citations, social reach and pipeline.",
      path: "/case-studies",
      jsonLd: [
        collectionPage({
          name: "Axy.digital Case Studies",
          description:
            "Real teams using Axy.digital to scale AI visibility and pipeline on autopilot.",
          url: `${SITE_URL}/case-studies`,
          items: studies
            .filter((s) => s.slug?.current)
            .map((s) => ({
              name: s.title,
              url: `/case-studies/${s.slug!.current}`,
            })),
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Case studies", url: "/case-studies" },
        ]),
      ],
    });
  },
  errorComponent: ({ error }) => (
    <section className="px-6 py-24 text-center text-white/70">{error.message}</section>
  ),
  notFoundComponent: () => (
    <section className="px-6 py-24 text-center text-white/70">Page not found.</section>
  ),
  component: CaseStudiesPage,
});

function CaseStudiesPage() {
  const { caseStudies } = Route.useLoaderData() as LoaderData;
  return (
    <ContentPage
      eyebrow="Case studies"
      title={
        <>
          Customers running <span className="text-gradient">Axy.digital</span>
        </>
      }
      description="Stories from founders and lean marketing teams shipping autonomous campaigns — from cold start to category default in a few weeks."
    >
      {caseStudies.length === 0 ? (
        <section className="px-6 py-24 text-center text-white/60">
          No case studies yet — check back soon.
        </section>
      ) : (
        <CaseStudiesSection
          items={caseStudies}
          eyebrow="All case studies"
          title="Real teams, real results"
        />
      )}
    </ContentPage>
  );
}
